import { useState, useEffect, useCallback } from "react";
import { AddListingBar } from "./components/AddListingBar";
import { ListingGrid } from "./components/ListingGrid";
import { ListingDetail } from "./components/ListingDetail";
import { EmptyState } from "./components/EmptyState";
import { Header } from "./components/Header";
import type { Listing } from "./types";
import "./index.css";

export function App() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchListings = useCallback(async () => {
    try {
      const res = await fetch("/api/listings");
      const data = await res.json();
      setListings(data.listings ?? []);
    } catch (err: any) {
      setError("Failed to load listings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleAddListing = async (url: string) => {
    setError(null);
    setSuccessMessage(null);
    setSaving(true);

    try {
      const res = await fetch("/api/listings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error ?? "Failed to save listing");
        return;
      }

      if (data.alreadyExists) {
        setSuccessMessage("Listing already saved");
        setSelectedListing(data.listing);
      } else {
        setSuccessMessage("Listing saved!");
        await fetchListings();
        setSelectedListing(data.listing);
      }
    } catch (err: any) {
      setError(err.message ?? "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await fetch(`/api/listings/${id}`, { method: "DELETE" });
      setListings((prev) => prev.filter((l) => l.id !== id));
      if (selectedListing?.id === id) {
        setSelectedListing(null);
      }
    } catch {
      setError("Failed to delete listing");
    }
  };

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <Header count={listings.length} />

      {selectedListing ? (
        <ListingDetail
          listing={selectedListing}
          onClose={() => setSelectedListing(null)}
          onDelete={() => handleDelete(selectedListing.id)}
        />
      ) : (
        <main className="flex-1 w-full max-w-7xl mx-auto px-6 pb-16">
          <AddListingBar
            onAdd={handleAddListing}
            loading={saving}
            error={error}
            successMessage={successMessage}
            onClearMessages={() => {
              setError(null);
              setSuccessMessage(null);
            }}
          />

          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="flex flex-col items-center gap-4">
                <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                <p className="text-muted-foreground text-sm tracking-wide">
                  Laddar bostäder...
                </p>
              </div>
            </div>
          ) : listings.length === 0 ? (
            <EmptyState />
          ) : (
            <ListingGrid
              listings={listings}
              onSelect={setSelectedListing}
              onDelete={handleDelete}
            />
          )}
        </main>
      )}
    </div>
  );
}

export default App;
